import { PLAQUE, LABEL } from './hud-theme';
/**
 * On-screen debug overlay — not game UI, a playtesting instrument. Exists to
 * make movement-netcode.md's Acceptance Criteria #2 (does parry feel fair at
 * ~80ms latency) actually checkable by a human: ping, current state,
 * hp/stamina, and a scrolling log of combat.result events so a tester can
 * see exactly what the server decided and when.
 */
/** The developer overlay's toggle — see `devTools`. */
const DEBUG_KEY = 'F8';

export class DebugHud {
  private readonly root: HTMLDivElement;
  private readonly objectiveEl: HTMLDivElement;
  /** The plaque's text, clamped to three lines on a phone — see the constructor. */
  private readonly objectiveText: HTMLSpanElement;
  private readonly statsEl: HTMLDivElement;
  private readonly logEl: HTMLDivElement;
  private readonly detail: HTMLDivElement;
  private logLines: string[] = [];
  /**
   * Developer build only (2026-10-16). The F1 key, its hint, the keybind
   * legend, the state/hp line and the combat log are a playtesting instrument;
   * a player asked for "the F1 debug" to be taken out of the interface. In a
   * production build the block holds ping / fps / frame cost and nothing
   * else, reachable only from the settings screen's performance overlay
   * toggle. Anything a player needs to know goes to a player-facing notice
   * (AbilityNotice / hub toast), never only to this log.
   */
  private readonly devTools: boolean;

  /**
   * `objective` — a one-line, always-visible reminder of what a run is
   * actually for. This whole overlay remains explicitly a playtesting
   * instrument (see the file comment), not a designed game HUD; this one
   * line is a minimal, disclosed stopgap for "the game feels unclear from
   * the very start" (no real objective/goal indicator anywhere in-game)
   * until a proper HUD (health/stamina bars, extraction marker, minimap)
   * gets designed as its own system.
   */
  constructor(
    container: HTMLElement,
    private readonly legend: string,
    objective?: string,
    devTools: boolean = import.meta.env.DEV,
  ) {
    this.devTools = devTools;
    // Restyled 2026-09-01. The panel used to carry one black monospace box for
    // both jobs: the objective a player reads, and the stats a developer reads.
    // That put a debug-console rectangle in the top-left ninth of a cartoon
    // dungeon, permanently. The root is now just a column — the two children
    // dress themselves, because they are for two different audiences.
    this.root = document.createElement('div');
    this.root.className = 'fixed top-2 left-2 z-[1000] pointer-events-none flex flex-col items-start gap-1.5';

    // The objective is the one thing here that was ever meant for a player, so
    // it is the one thing here that is dressed like the game. Width is capped
    // against the viewport as well as in rem: at 390px the old 360px box was
    // 92% of the screen and the goal compass drew straight through it.
    this.objectiveEl = document.createElement('div');
    this.objectiveEl.dataset.hud = 'objective';
    this.objectiveEl.className = `${PLAQUE} ${LABEL} px-3 py-2 text-[12px] leading-snug font-semibold `
      + 'whitespace-pre-wrap max-w-[min(22rem,58vw)]';
    /**
     * The text lives in a span so a phone can clamp it (2026-09-08).
     *
     * On touch this paragraph is capped at three lines, and the cap used to be
     * a `max-height` computed from the line height — which shed a sliver of a
     * fourth line, because the plaque is `border-box` and the arithmetic owed
     * two pixels to its own border. Adding the two back still left a hairline
     * of ascenders: fractional line boxes are not a thing to do arithmetic
     * against. `-webkit-line-clamp` counts lines, which is the actual unit.
     *
     * It needs `display: -webkit-box`, and putting that on the plaque itself
     * is what was tried and rejected the first time — the plaque paints its
     * own background and stopped growing while its text kept going. On an
     * inner span it costs the plaque nothing.
     */
    this.objectiveText = document.createElement('span');
    this.objectiveText.dataset.hud = 'objective-text';
    this.objectiveText.textContent = objective ?? '';
    this.objectiveEl.appendChild(this.objectiveText);
    this.objectiveEl.hidden = !objective;

    this.statsEl = document.createElement('div');
    this.logEl = document.createElement('div');
    this.logEl.style.cssText = 'margin-top:6px;border-top:1px solid #444;padding-top:6px;opacity:0.85;';

    // HUD slice (2026-08-24) — user report: "the interface for my abilities,
    // HP, mana, and energy is not UI-friendly at all."
    //
    // A real HUD now exists (`combat-hud.ts` + `action-bar.ts`), so this
    // overlay is back to being only what its own file comment always said it
    // was: a playtesting instrument. It occupied the top-left ninth of the
    // screen with a monospace stats block, a 250-character keybind legend and
    // a scrolling combat log, all of it permanently on top of the game.
    //
    // Collapsed by default, toggled with F8 in a developer build (F1–F4 target
    // yourself and your squad since 2026-10-16). The objective line stays visible
    // unconditionally — it is the one part of this panel that was ever meant
    // for a player, and nothing has replaced it yet.
    // Unchanged in spirit: monospace on black, because it is an instrument and
    // should not pretend to be part of the game. It just no longer wraps the
    // objective in its chrome.
    this.detail = document.createElement('div');
    this.detail.style.cssText = [
      'font-family:monospace', 'font-size:12px', 'color:#dfe',
      'background:rgba(0,0,0,0.6)', 'padding:8px 10px', 'border-radius:6px',
      'white-space:pre', 'max-width:360px',
    ].join(';');
    this.detail.append(this.statsEl, this.logEl);
    this.detail.hidden = true;

    // No on-screen "F8 debug" hint (2026-10-16 QA): it sat in the top-left of
    // every dev-server session and read as part of the game. F8 still toggles
    // the block silently in a developer build; the settings screen's "show
    // performance overlay" is the route everyone else has.
    this.logEl.hidden = !this.devTools;

    this.root.append(this.objectiveEl, this.detail);
    container.appendChild(this.root);

    if (this.devTools) window.addEventListener('keydown', this.onKeyDown);
  }

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.code !== DEBUG_KEY) return;
    e.preventDefault();
    this.detail.hidden = !this.detail.hidden;
  };

  /**
   * Open or close the stats block from outside, for the settings screen's
   * "show performance overlay". F8 still toggles it from here afterwards —
   * this sets the starting state rather than locking it.
   */
  /**
   * Rewrites the objective line mid-run (2026-09-07).
   *
   * The line used to be a constant handed in at construction, which was fine
   * while it was true everywhere. It is not: on the Cellars and the Ossuary
   * the stairwell at the end of a run goes *down*, not out (run-lifecycle.md
   * Core Rule #3), and a banner promising "extract" on a floor that cannot
   * extract is the reason a player who did everything right reported that
   * nothing happened. The client only learns which floor it is on after the
   * room joins, so the line has to be settable afterwards.
   */
  setObjective(objective: string): void {
    this.objectiveText.textContent = objective;
    this.objectiveEl.hidden = !objective;
  }

  setDetailVisible(visible: boolean): void {
    this.detail.hidden = !visible;
  }

  /** Whether the collapsible block is open — the caller can skip building its stats string when nobody is looking at it. */
  get detailVisible(): boolean {
    return !this.detail.hidden;
  }

  /** `fps`/`frameMs`/`worstFrameMs` (Game Feel Polish Phase 5) come from `sim/frame-timer.ts`; omitted leaves the line out entirely, so callers that don't measure are unaffected. */
  updateStats(
    stats: {
      pingMs: number; state: string; hp: number; stamina: number;
      fps?: number; frameMs?: number; worstFrameMs?: number;
    },
    extra?: string,
  ): void {
    const perf =
      `ping: ${stats.pingMs.toFixed(0)}ms\n` +
      (stats.fps !== undefined
        ? `fps: ${stats.fps.toFixed(0)}  frame: ${(stats.frameMs ?? 0).toFixed(1)}ms  worst: ${(stats.worstFrameMs ?? 0).toFixed(1)}ms\n`
        : '');
    if (!this.devTools) { this.statsEl.textContent = perf.trimEnd(); return; }
    this.statsEl.textContent =
      perf +
      `state: ${stats.state}\n` +
      `hp: ${stats.hp.toFixed(0)}  stamina: ${stats.stamina.toFixed(0)}\n` +
      (extra ? `${extra}\n` : '') +
      this.legend;
  }

  logCombatEvent(line: string): void {
    if (!this.devTools) return;
    this.logLines.unshift(`${new Date().toLocaleTimeString()}  ${line}`);
    this.logLines = this.logLines.slice(0, 8);
    this.logEl.textContent = this.logLines.join('\n');
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    this.root.remove();
  }
}
